import React from "react";
import { Box, Typography, Avatar, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom";
import SEOHead from "../SEOHead";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import { acme, acmeMarketingSectionTitleSx, acmeMarketingEyebrowSx } from "../../theme/acmeTheme";
import { Button, Card, CardContent, Chip } from "../ui";
import AboutHero from "./AboutHero";
import { aboutValues, aboutTeam } from "./aboutUsData";

const ATIKUT_LOGO = `${process.env.PUBLIC_URL || ""}/images/logo_company/logo_black_atikut.svg`;

const sectionEyebrowSx = {
  ...acmeMarketingEyebrowSx,
  color: acme.colors.mutedForegroundSolid,
  fontSize: "0.75rem",
  letterSpacing: "0.12em",
  mb: 1.5,
  display: "block",
};

const sectionTitleSx = {
  ...acmeMarketingSectionTitleSx,
  color: acme.colors.foreground,
  fontSize: { xs: "1.75rem", md: "2.25rem" },
  mb: 2,
};

const flatCardSx = { boxShadow: "none" };

function SectionHeading({ eyebrow, title, centered = false }) {
  return (
    <Box sx={{ textAlign: centered ? "center" : "left", mb: { xs: 5, md: 6 } }}>
      <Typography component="span" sx={sectionEyebrowSx}>
        {eyebrow}
      </Typography>
      <Typography component="h2" sx={sectionTitleSx}>
        {title}
      </Typography>
    </Box>
  );
}

function AboutUs() {
  return (
    <>
      <SEOHead pageName="about" />

      <Box sx={{ backgroundColor: acme.colors.pageBg, color: acme.colors.foreground }}>
        <AboutHero />

        <Box
          component="section"
          sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 4 } }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: { xs: 5, md: 8 },
                alignItems: "center",
              }}
            >
              <Card
                sx={{
                  ...flatCardSx,
                  minHeight: { xs: 240, md: 380 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: acme.colors.background,
                  p: { xs: 4, md: 6 },
                }}
              >
                <MuiLink
                  href="https://atikut.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Atikut"
                  sx={{
                    display: "inline-flex",
                    lineHeight: 0,
                    opacity: 0.9,
                    transition: "opacity 0.15s",
                    "&:hover": { opacity: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={ATIKUT_LOGO}
                    alt="Atikut"
                    sx={{
                      width: "100%",
                      maxWidth: 220,
                      height: "auto",
                      display: "block",
                    }}
                  />
                </MuiLink>
              </Card>

              <Box>
                <SectionHeading
                  eyebrow="Our story"
                  title="Built by Atikut, for marketers"
                />
                <Typography sx={{ color: acme.colors.mutedForegroundSolid, lineHeight: 1.8, mb: 2.5 }}>
                  Mailsball is a product of{" "}
                  <MuiLink
                    href="https://atikut.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: acme.colors.foreground, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    Atikut
                  </MuiLink>
                  , an Estonian company based in Tallinn. We built an all-in-one marketing
                  platform with email and SMS campaigns, automation, and analytics
                  — so you can grow your audience without juggling half a dozen tools.
                </Typography>
                <Typography sx={{ color: acme.colors.mutedForegroundSolid, lineHeight: 1.8, mb: 2.5 }}>
                  Atikut was founded from a simple frustration: existing email marketing tools
                  were either too complex for small businesses or too limited for growing
                  companies. Mailsball is our answer — powerful enough to scale, simple enough
                  to launch your first campaign today.
                </Typography>
                <Typography sx={{ color: acme.colors.mutedForegroundSolid, lineHeight: 1.8, mb: 2.5 }}>
                  Today, Mailsball serves over 1,000 businesses across 15+ countries, delivering
                  100+ million emails annually with industry-leading deliverability rates.
                </Typography>
                <Typography sx={{ color: acme.colors.foreground, lineHeight: 1.8, fontWeight: 500 }}>
                  Created by Aleksandr Khrustalov, CEO & Founder of Atikut.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          component="section"
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 4 },
            backgroundColor: acme.colors.background,
            borderTop: `1px solid ${acme.colors.border}`,
            borderBottom: `1px solid ${acme.colors.border}`,
          }}
        >
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <SectionHeading eyebrow="What we stand for" title="Our values" centered />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 2.5,
              }}
            >
              {aboutValues.map((value) => (
                <Card
                  key={value.title}
                  sx={{
                    ...flatCardSx,
                    height: "100%",
                    transition: "border-color 0.2s ease",
                    "&:hover": {
                      borderColor: `${acme.colors.brand}44`,
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: acme.radius.md,
                        backgroundColor: acme.colors.brandMuted,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      {value.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 600, fontSize: "1.0625rem", mb: 1 }}>
                      {value.title}
                    </Typography>
                    <Typography
                      sx={{ color: acme.colors.mutedForegroundSolid, lineHeight: 1.7, fontSize: "0.9375rem" }}
                    >
                      {value.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>

        <Box component="section" sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 4 } }}>
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <SectionHeading eyebrow="The people behind Mailsball" title="Meet our team" centered />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                gap: 2.5,
              }}
            >
              {aboutTeam.map((member) => (
                <Card key={member.name} sx={{ ...flatCardSx, height: "100%" }}>
                  <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
                    <Avatar
                      src={member.avatar}
                      alt={member.name}
                      sx={{
                        width: 96,
                        height: 96,
                        mx: "auto",
                        mb: 2.5,
                        backgroundColor: acme.colors.muted,
                        color: acme.colors.foreground,
                        fontSize: "1.75rem",
                        fontWeight: 600,
                        border: `2px solid ${acme.colors.border}`,
                      }}
                    >
                      {member.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </Avatar>
                    <Typography sx={{ fontWeight: 600, fontSize: "1.125rem", mb: 0.5 }}>
                      {member.name}
                    </Typography>
                    <Typography
                      sx={{
                        color: acme.colors.brand,
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        mb: 1.5,
                      }}
                    >
                      {member.role}
                    </Typography>
                    <Typography
                      sx={{
                        color: acme.colors.mutedForegroundSolid,
                        fontSize: "0.875rem",
                        lineHeight: 1.6,
                        mb: 2.5,
                      }}
                    >
                      {member.description}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        justifyContent: "center",
                      }}
                    >
                      {member.skills.map((skill) => (
                        <Chip
                          key={skill}
                          label={skill}
                          sx={{
                            backgroundColor: acme.colors.muted,
                            borderColor: acme.colors.border,
                            color: acme.colors.mutedForegroundSolid,
                          }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, pb: { xs: 8, md: 12 } }}>
          <Box sx={{ maxWidth: 1200, mx: "auto" }}>
            <Card
              sx={{
                ...flatCardSx,
                textAlign: "center",
                p: { xs: 4, md: 6 },
                backgroundColor: acme.colors.background,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: acme.radius.md,
                  backgroundColor: acme.colors.brandMuted,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                }}
              >
                <HandshakeOutlinedIcon sx={{ fontSize: 32, color: acme.colors.brand }} />
              </Box>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  letterSpacing: "-0.025em",
                  mb: 1.5,
                }}
              >
                Ready to join 1,000+ successful businesses?
              </Typography>
              <Typography
                sx={{
                  color: acme.colors.mutedForegroundSolid,
                  maxWidth: 520,
                  mx: "auto",
                  lineHeight: 1.7,
                  mb: 3.5,
                }}
              >
                Start your email marketing journey with Mailsball today. No setup fees, no
                long-term contracts — just results.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  justifyContent: "center",
                }}
              >
                <Button component={Link} to="/register" sx={{ borderRadius: acme.radius.full, px: 3.5 }}>
                  Start free trial
                </Button>
                <Button
                  component={Link}
                  to="/pricing"
                  variant="outline"
                  sx={{ borderRadius: acme.radius.full, px: 3.5 }}
                >
                  View pricing
                </Button>
              </Box>
            </Card>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default AboutUs;
